import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import './Availability.css';
import AppIcon from '../common/AppIcon';
import { getApiBaseUrl } from '../../services/api';
import { onDataChange, DATA_TYPES, shouldRefresh } from '../../services/dataEvents';

const Availability = () => {
  const [availability, setAvailability] = useState([]);
  const [onCallDoctors, setOnCallDoctors] = useState([]);
  const [upcomingShifts, setUpcomingShifts] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const apiUrl = getApiBaseUrl();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all shifts from the backend
      const shiftsRes = await axios.get(`${apiUrl}/api/processedresult`);
      const allShifts = Array.isArray(shiftsRes.data) ? shiftsRes.data : [];
      
      // Get today's date in dd-mm-yyyy format
      const today = new Date();
      const todayStr = `${today.getDate().toString().padStart(2, '0')}-${(today.getMonth() + 1).toString().padStart(2, '0')}-${today.getFullYear()}`;
      
      // Filter today's shifts - handle both shift_date format and separate day/month/year format
      const todayShifts = allShifts.filter(shift => {
        if (shift.shift_date) {
          return shift.shift_date === todayStr;
        } else if (shift.shift_day && shift.shift_month && shift.shift_year) {
          const shiftDateStr = `${shift.shift_day.toString().padStart(2, '0')}-${shift.shift_month.toString().padStart(2, '0')}-${shift.shift_year}`;
          return shiftDateStr === todayStr;
        }
        return false;
      });
      setOnCallDoctors(todayShifts);
      
      // Get upcoming shifts (next 7 days)
      const next7Days = [];
      for (let i = 1; i <= 7; i++) {
        const futureDate = new Date();
        futureDate.setDate(today.getDate() + i);
        const futureDateStr = `${futureDate.getDate().toString().padStart(2, '0')}-${(futureDate.getMonth() + 1).toString().padStart(2, '0')}-${futureDate.getFullYear()}`;
        next7Days.push(futureDateStr);
      }
      
      const upcoming = allShifts.filter(shift => {
        let shiftDateStr;
        if (shift.shift_date) {
          shiftDateStr = shift.shift_date;
        } else if (shift.shift_day && shift.shift_month && shift.shift_year) {
          shiftDateStr = `${shift.shift_day.toString().padStart(2, '0')}-${shift.shift_month.toString().padStart(2, '0')}-${shift.shift_year}`;
        } else {
          return false;
        }
        return next7Days.includes(shiftDateStr);
      }).map(shift => ({
        ...shift,
        shift_date: shift.shift_date || `${shift.shift_day.toString().padStart(2, '0')}-${shift.shift_month.toString().padStart(2, '0')}-${shift.shift_year}`
      }));
      setUpcomingShifts(upcoming);
      
      // Fetch hospitals from backend for real-time data
      const hospitalsRes = await axios.get(`${apiUrl}/api/hospitals`);
      const hospitals = (Array.isArray(hospitalsRes.data) ? hospitalsRes.data : []).map(hospital => ({
        code: hospital.code,
        name: hospital.name,
        slots: hospital.monthlySlots ? Math.ceil(hospital.monthlySlots / 30) : 2 // Calculate daily slots from monthly
      }));
      
      const hospitalAvailability = hospitals.map(hospital => {
        const hospitalShifts = todayShifts.filter(shift => shift.location === hospital.code);
        const booked = hospitalShifts.length;
        const open = Math.max(0, hospital.slots - booked);
        
        return {
          ...hospital,
          booked,
          open,
          totalSlots: hospital.slots
        };
      });
      
      setAvailability(hospitalAvailability);
      
    } catch (err) {
      setError('Failed to load availability data');
      console.error('Error loading availability:', err);
    } finally {
      setLoading(false);
    }
  }, [apiUrl]);

  useEffect(() => {
    load();
    
    const interval = setInterval(load, 30000);
    const unsub = onDataChange((type) => {
      if (shouldRefresh(type, [DATA_TYPES.SHIFTS, DATA_TYPES.HOSPITALS, DATA_TYPES.GROUP, DATA_TYPES.ALL])) {
        load();
      }
    });
    return () => {
      clearInterval(interval);
      unsub();
    };
  }, [load]);

  const getHospitalName = (code) => {
    // Use the availability data which contains real hospital names
    const hospital = availability.find(h => h.code === code);
    return hospital ? hospital.name : code;
  };

  const formatShiftTime = (shiftTime) => {
    if (Array.isArray(shiftTime) && shiftTime.length === 2) {
      return `${shiftTime[0]}:${shiftTime[1].toString().padStart(2, '0')}`;
    }
    return '24 Hours';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const [day, month, year] = dateStr.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  if (loading) return (
    <div className="availability-container">
      <div className="loading-spinner">Loading availability...</div>
    </div>
  );

  if (error) return (
    <div className="availability-container">
      <div className="error-message">Error: {error}</div>
      <button onClick={load} className="retry-btn">Retry</button>
    </div>
  );

  return (
    <div className="availability-container">
      <div className="availability-header">
        <h2 className="icon-with-label">
          <AppIcon name="availability" size={28} strokeWidth={2.25} className="app-icon--primary" />
          Availability Dashboard
        </h2>
        <button onClick={load} className="refresh-btn" disabled={loading}>
          <AppIcon name="refresh" size={18} strokeWidth={2.25} className={loading ? 'spin' : ''} />
          {loading ? 'Refreshing...' : 'Refresh Data'}
        </button>
      </div>
      
      {/* Who is On Call Today */}
      <div className="on-call-section">
        <h3 className="icon-with-label">
          <AppIcon name="doctors" size={22} strokeWidth={2.25} />
          Who is On Call Today
        </h3>
        <div className="on-call-grid">
          {onCallDoctors.length > 0 ? (
            onCallDoctors.map((doctor, index) => (
              <div key={index} className="on-call-card">
                <div className="doctor-name icon-with-label">
                  <AppIcon name="doctors" size={20} strokeWidth={2} />
                  {doctor.name}
                </div>
                <div className="hospital-location icon-with-label">
                  <AppIcon name="hospitals" size={18} strokeWidth={2} className="app-icon--muted" />
                  {getHospitalName(doctor.location)}
                </div>
                <div className="shift-time icon-with-label">
                  <AppIcon name="time" size={18} strokeWidth={2} className="app-icon--muted" />
                  {formatShiftTime(doctor.shift_time)}
                </div>
                <div className="status-badge on-call">ON CALL</div>
              </div>
            ))
          ) : (
            <div className="no-on-call">
              <p>No doctors currently on call</p>
              <small>Check the schedule to see upcoming shifts</small>
            </div>
          )}
        </div>
      </div>

      {/* Hospital Availability */}
      <div className="availability-section">
        <h3 className="icon-with-label">
          <AppIcon name="hospitals" size={22} strokeWidth={2.25} />
          Hospital Availability
        </h3>
        <div className="availability-table">
          <table>
            <thead>
              <tr>
                <th>Location</th>
                <th>Slots Booked</th>
                <th>Open</th>
                <th>Doctors On Call</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {(availability || []).map((item, index) => {
                const hospitalDoctors = onCallDoctors.filter(doc => doc.location === item.code);
                return (
                  <tr key={index}>
                    <td>{item.name}</td>
                    <td>{item.booked}</td>
                    <td>{item.open}</td>
                    <td>
                      {hospitalDoctors.length > 0 ? (
                        <div className="doctors-list">
                          {hospitalDoctors.map((doc, idx) => (
                            <span key={idx} className="doctor-tag">
                              {doc.name}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <span className="no-doctors">None</span>
                      )}
                    </td>
                    <td>
                      <span className={`status-badge ${item.open > 0 ? 'available' : 'full'}`}>
                        {item.open > 0 ? 'Available' : 'Full'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upcoming Shifts */}
      <div className="upcoming-section">
        <h3 className="icon-with-label">
          <AppIcon name="schedule" size={22} strokeWidth={2.25} />
          Upcoming Shifts (Next 7 Days)
        </h3>
        <div className="upcoming-shifts">
          {upcomingShifts.length > 0 ? (
            <div className="upcoming-grid">
              {upcomingShifts.map((shift, index) => (
                <div key={index} className="upcoming-card">
                  <div className="shift-date">{formatDate(shift.shift_date)}</div>
                  <div className="doctor-name icon-with-label">
                    <AppIcon name="doctors" size={18} strokeWidth={2} />
                    {shift.name}
                  </div>
                  <div className="hospital-location icon-with-label">
                    <AppIcon name="hospitals" size={16} strokeWidth={2} className="app-icon--muted" />
                    {getHospitalName(shift.location)}
                  </div>
                  <div className="shift-time icon-with-label">
                    <AppIcon name="time" size={16} strokeWidth={2} className="app-icon--muted" />
                    {formatShiftTime(shift.shift_time)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-upcoming">
              <p>No upcoming shifts scheduled for the next 7 days</p>
              <small>Use the AI schedule generator to create shifts</small>
            </div>
          )}
        </div>
      </div>

      <div className="last-updated">
        Last updated: {new Date().toLocaleString()}
      </div>
    </div>
  );
};

export default Availability;


